import clsx from 'clsx'
import GeoContext from 'contexts/geoContext'
import { addRegionToPath } from 'lib/url/searchUrlUtils'
import { useCallback, useContext, useMemo, useRef } from 'react'
import * as React from 'react'
import { Link } from 'react-router-dom'
import type { LinkProps } from 'react-router-dom'
import styled from 'styled-components'
import { scrollToId } from 'lib/window/scrollUtils'

/*
  This component is to be used as a 'wrapper' for things that are clickable
  and link to another URL without imparting any styles to it.
*/
const ClickableElement = styled.a`
  display: block;

  &.relative {
    position: relative;
  }
`

const clickMoveTolerance = 3
const hashScrollRetryDelayMs = 100
const hashScrollRetryCount = 10

function getLinkHash(to: LinkProps['to']): string | undefined {
  if (typeof to === 'string') {
    const hashIndex = to.indexOf('#')
    return hashIndex >= 0 ? to.slice(hashIndex) : undefined
  }

  return to.hash
}

function scrollToHashTarget(to: LinkProps['to'], attempt = 0) {
  const hash = getLinkHash(to)

  if (!hash) return
  if (hash === '#' || hash === '#top') {
    window.scrollTo(0, 0)
    return
  }

  const targetId = decodeURIComponent(hash.replace(/^#/, ''))
  if (document.getElementById(targetId)) {
    scrollToId(targetId)
  } else if (attempt < hashScrollRetryCount) {
    window.setTimeout(() => {
      scrollToHashTarget(to, attempt + 1)
    }, hashScrollRetryDelayMs)
  }
}

function isModifiedClick(event: React.MouseEvent<HTMLAnchorElement>): boolean {
  return event.metaKey || event.altKey || event.ctrlKey || event.shiftKey || event.button !== 0
}

const InternalHashLink = React.forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const { onClick, target, to, ...rest } = props

  const handleClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event)

    if (event.defaultPrevented || target === '_blank' || isModifiedClick(event)) {
      return
    }

    window.setTimeout(() => {
      scrollToHashTarget(to)
    }, 0)
  }, [onClick, target, to])

  return <Link {...rest} ref={ref} target={target} to={to} onClick={handleClick} />
})

InternalHashLink.displayName = 'InternalHashLink'

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement | HTMLAnchorElement>, Omit<React.AnchorHTMLAttributes<HTMLAnchorElement | HTMLButtonElement>, 'type'> {
  to?: string
  preventSwipeClick?: boolean
  activeClassName?: string
  relative?: boolean
  ref?: React.Ref<HTMLAnchorElement>
}

function ClickableLink(props: Props) {
  const {
    to,
    onMouseDown,
    onClick,
    preventSwipeClick,
    target,
    rel,
    relative,
    className,
    activeClassName: _activeClassName,
    ...rest
  } = props

  const lastMouseDown = useRef({ x: -clickMoveTolerance, y: -clickMoveTolerance })
  const { currentRegionCode } = useContext(GeoContext)

  const linkTo = useMemo(() => addRegionToPath(to ?? '', currentRegionCode), [currentRegionCode, to])

  const onLinkClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!e.defaultPrevented) {
      if (preventSwipeClick) {
        const mouseMovementX = Math.abs(e.screenX - lastMouseDown.current.x)
        const mouseMovementY = Math.abs(e.screenY - lastMouseDown.current.y)
        if (mouseMovementX > clickMoveTolerance || mouseMovementY > clickMoveTolerance) {
          e.preventDefault()
          return
        }
      }
      onClick?.(e)
    }
  }, [onClick, preventSwipeClick])

  const onLinkMouseDown = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    onMouseDown?.(e)

    lastMouseDown.current = { x: e.screenX, y: e.screenY }
  }, [onMouseDown])

  return <ClickableElement
    {...rest}
    className={clsx(className, { relative })}
    onClick={onLinkClick}
    onMouseDown={preventSwipeClick ? onLinkMouseDown : onMouseDown}
    as={to ? InternalHashLink : undefined}
    to={linkTo}
    target={target ?? undefined}
    rel={rel ?? (target === '_blank' ? 'noopener' : undefined)}
  />
}

export default React.memo(ClickableLink)
